import React, { ChangeEventHandler, useCallback } from 'react';
import { FunctionComponent } from 'react';
import { Field } from '../../Field/Field';
import { Input, FieldContainer } from '../../Components';
import { Validator } from '../../Field';

export type EmailFieldProps = {
    id?: string;
    name: string;
    label: string;
    description?: string;
    initialValue?: string;
    required?: boolean;
    disabled?: boolean;
};

export const EmailField: FunctionComponent<EmailFieldProps> = ({
    id,
    name,
    label,
    description,
    initialValue,
    required = false,
    disabled,
}) => {
    const validate = useCallback<Validator<string>>(async (value) => {
        const errorMessage = validateEmailAddress(value);

        if (errorMessage) {
            return { pass: false, message: errorMessage };
        }

        return { pass: true };
    }, []);

    const fieldProps = {
        id,
        name,
        description,
        initialValue,
        onBlurValidate: validate,
        onSubmitValidate: validate,
        required,
    };

    return (
        <Field<string> {...fieldProps}>
            {({
                fieldId,
                value,
                setValue,
                onBlur,
                errors,
                isValid,
                descriptionId,
                errorId,
                required,
                requiredStrategy,
            }) => {
                return (
                    <FieldContainer
                        errorId={errorId}
                        errors={errors}
                        descriptionId={descriptionId}
                        description={description}
                    >
                        <Input
                            id={fieldId}
                            type="email"
                            name={name}
                            label={label}
                            value={value}
                            onChange={setValue}
                            onBlur={onBlur}
                            aria-invalid={isValid === false}
                            aria-errormessage={errorId}
                            aria-describedby={descriptionId}
                            required={required}
                            requiredStrategy={requiredStrategy}
                            disabled={disabled}
                        />
                    </FieldContainer>
                );
            }}
        </Field>
    );
};

export const validateEmailAddress = (value: string): string | void => {
    const [handle, domain, ...excessAtSymbols] = value.split('@');

    if (typeof value !== 'string') {
        throw new Error('Expected string');
    }

    if (value.length < 5) {
        return `Email address is too short`;
    }

    if (!value.includes('@')) {
        return 'Missing @ symbol';
    }

    if (excessAtSymbols && excessAtSymbols.length > 0) {
        return `Too many @ symbols`;
    }

    if (!handle) {
        return `Missing handle`;
    }

    if (['.', '<'].some((illegalCharacter) => value.startsWith(illegalCharacter))) {
        return `Email address cannot start with illegal character`;
    }

    if (value.endsWith('.')) {
        return `Email address cannot end with a period`;
    }

    if (['”', '\t', '\n', ' '].some((illegalCharacter) => value.includes(illegalCharacter))) {
        return `Email address cannot contain an illegal character`;
    }

    if (['-'].some((illegalCharacter) => domain.startsWith(illegalCharacter))) {
        return `Email domain cannot start with an illegal character`;
    }

    if (!domain.includes('.')) {
        return `Invalid domain`;
    }

    const splitOnConsecutiveDomainDots = domain.split('.');
    if (splitOnConsecutiveDomainDots.some((part) => !part || part === '')) {
        return `Invalid domain`;
    }

    try {
        new URL(`https://${domain}`);
    } catch (err) {
        return `Invalid domain`;
    }
};
